import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Link } from "react-router-dom";

import {
  ApiBase_slashless,
  deleteVideoGallery,
  loadVideoGallery,
  loadVideoGalleries,
  setVideoGallerySortOrder,
} from "../utils/Network";
import { isAdmin } from "../utils";
import VideoGalleryPickerModal from "./VideoGalleryPickerModal";

function videoListSrc(path) {
  if (path == null || path === "") return "";
  const s = String(path);
  if (/^https?:\/\//i.test(s)) return s;
  const base = String(ApiBase_slashless).replace(/\/$/, "");
  return s.startsWith("/") ? `${base}${s}` : `${base}/${s}`;
}

function hasDisplayTag(tag) {
  if (tag == null) return false;
  const s = String(tag).trim();
  return s !== "" && s.toLowerCase() !== "null";
}

export default function VideoGalleryListContent({ page, urlParams }) {
  const [loading, setLoading] = useState(0);
  const [pageloading, setPageLoading] = useState(false);
  const [data, setData] = useState(null);
  const [show_delete, setShowDelete] = useState(false);
  const [count, setCount] = useState(null);
  const [pages, setPages] = useState(null);
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [replacePicker, setReplacePicker] = useState({
    open: false,
    id: null,
    meta: null,
  });

  function getData() {
    const filter_query = window.location.search || "";
    setPageLoading(true);
    loadVideoGalleries(filter_query, page).then((res) => {
      if (res.results) {
        setData(res.results);
      }
      if (res.count != null) {
        setCount(res.count);
        const page_count =
          res.count % 10 === 0
            ? parseInt(res.count / 10, 10)
            : parseInt(res.count / 10, 10) + 1;
        setPages(range(1, page_count));
      }
      setPageLoading(false);
    });
  }

  useEffect(() => {
    getData();
  }, [page]);

  function range(start, end) {
    return Array.apply(0, Array(end)).map((_, index) => index + start);
  }

  const pageHref = (pageNum) => {
    const q = new URLSearchParams(
      urlParams && urlParams.toString ? urlParams.toString() : ""
    );
    q.set("page", String(pageNum));
    return `/cms/videogallerylist/?${q.toString()}`;
  };

  const handleDelete = (id) => {
    setLoading(id);
    deleteVideoGallery(id).then(() => {
      setLoading(0);
      getData();
    });
  };

  const showDelete = (e) => {
    e.preventDefault();
    setShowDelete(!show_delete);
  };

  const openAdd = () => {
    setAddPickerOpen(true);
  };

  const openEdit = (id) => {
    loadVideoGallery(id).then((vg) => {
      setReplacePicker({
        open: true,
        id,
        meta: {
          title: vg.title || "",
          tag: vg.tag || "",
          language: vg.language,
          image: vg.image || "",
        },
      });
    });
  };

  const closeAddPicker = () => {
    setAddPickerOpen(false);
  };

  const closeReplacePicker = () => {
    setReplacePicker({ open: false, id: null, meta: null });
  };

  const onListAddDone = () => {
    window.location.reload();
  };

  const onReplaceDone = () => {
    window.location.reload();
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination || !data) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const talent = data.find((x) => String(x.id) === String(draggableId));
    if (!talent) return;
    const temp = [...data];

    temp.splice(source.index, 1);
    temp.splice(destination.index, 0, talent);

    const ids = temp.map((child) => child.id);

    setVideoGallerySortOrder(ids).then(() => {});

    setData(temp);
  };

  const showPagination = pages && pages.length > 1;

  return (
    <>
      <div className="content-wrapper cms-videogallerylist-page cms-ratings-surface">
        <section className="content padding-top-sm-0">
          <div className="container-fluid cms-videogallerylist-inner">
            <div className="row">
              <div className="col-md-12">
                <div className="box box-primary">
                  <div className="box-header with-border">
                    <h3 className="box-title">
                      Video Gallery{" "}
                      <span style={{ fontSize: 11 }}>({count ?? "—"})</span>
                    </h3>
                    {isAdmin() && (
                      <>
                        <button
                          type="button"
                          title="Add New"
                          className="btn btn-cm margin-r-5 pull-right mt-2p"
                          onClick={openAdd}
                        >
                          <i className="fa fa-plus" />
                        </button>
                        <button
                          type="button"
                          title="Show Delete Button"
                          className="btn btn-cm margin-r-5 pull-right mt-2p"
                          onClick={showDelete}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="box-body p-4">
                    <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId="videogallery-grid" direction="vertical">
                        {(dropProvided) => (
                          <div
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            className="cms-videogallerylist-grid"
                          >
                            {pageloading && !data && (
                              <div className="cms-videogallerylist-grid-loading text-muted">
                                Loading…
                              </div>
                            )}
                            {data &&
                              data.map((item, index) => (
                                <Draggable
                                  key={item.id}
                                  draggableId={String(item.id)}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`cms-videogallerylist-card${
                                        loading === item.id ? " is-loading" : ""
                                      }${snapshot.isDragging ? " is-dragging" : ""}`}
                                    >
                                      <div
                                        className="cms-videogallerylist-card__grip"
                                        {...provided.dragHandleProps}
                                        title="Drag to reorder"
                                      >
                                        <i className="fa fa-bars" aria-hidden="true" />
                                      </div>
                                      <button
                                        type="button"
                                        className="cms-videogallerylist-card__media"
                                        onClick={() => openEdit(item.id)}
                                      >
                                        {item.file != null ? (
                                          <video
                                            className="cms-videogallerylist-card__video"
                                            preload="metadata"
                                            muted
                                            playsInline
                                          >
                                            <source src={videoListSrc(item.file)} />
                                          </video>
                                        ) : (
                                          <div className="cms-videogallerylist-card__placeholder">
                                            <i className="fa fa-film" />
                                          </div>
                                        )}
                                      </button>
                                      <div className="cms-videogallerylist-card__body">
                                        <div
                                          className="cms-videogallerylist-card__title"
                                          title={item.title}
                                        >
                                          {item.title || `Video #${item.id}`}
                                        </div>
                                        {hasDisplayTag(item.tag) ? (
                                          <div className="cms-videogallerylist-card__tag">
                                            {item.tag}
                                          </div>
                                        ) : null}
                                        <div className="cms-videogallerylist-card__date text-muted">
                                          {item.created_at &&
                                            item.created_at.split("T")[0]}
                                        </div>
                                      </div>
                                      <div className="cms-videogallerylist-card__actions">
                                        {show_delete && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (
                                                window.confirm(
                                                  "Are you sure you wish to delete this item?"
                                                )
                                              ) {
                                                handleDelete(item.id);
                                              }
                                            }}
                                            className="btn btn-cm btn-sm btn-cm-danger"
                                          >
                                            <i className="fa fa-trash" />
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className="btn btn-cm btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openEdit(item.id);
                                          }}
                                        >
                                          <i className="fa fa-edit" />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                            {dropProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                </div>

                {showPagination && (
                  <ul className="pagination cms-pagination dark m-0 float-right cms-videogallerylist-pagination">
                    <li className="page-item">
                      <Link className="page-link" to="#">
                        «
                      </Link>
                    </li>
                    {pages.map((page_number) => (
                      <li key={page_number} className="page-item">
                        <a
                          className={
                            page_number == page ? "page-link active" : "page-link"
                          }
                          href={pageHref(page_number)}
                        >
                          {page_number}
                        </a>
                      </li>
                    ))}
                    <li className="page-item">
                      <Link className="page-link" to="">
                        »
                      </Link>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <VideoGalleryPickerModal
        isOpen={addPickerOpen}
        closeGallery={closeAddPicker}
        languageId={null}
        listAddMode
        onListAdded={onListAddDone}
        onVideoSelected={() => {}}
      />

      <VideoGalleryPickerModal
        isOpen={replacePicker.open && !!replacePicker.meta}
        closeGallery={closeReplacePicker}
        languageId={replacePicker.meta?.language}
        replaceVideoId={replacePicker.id}
        replaceMeta={replacePicker.meta}
        onReplaceComplete={onReplaceDone}
        onVideoSelected={() => {}}
      />
    </>
  );
}
